import { Component, AfterViewInit, ViewChild, ElementRef, Input, OnInit } from '@angular/core';
import { Chart, TooltipItem } from 'chart.js';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';
import { Hit } from 'src/app/models/sector-industries/sector-industries.model';

@Component({
    selector: 'app-ratios-chart',
    templateUrl: './ratios-chart.component.html',
    styleUrls: ['./ratios-chart.component.scss'],
    standalone: false
})
export class RatiosChartComponent implements OnInit, AfterViewInit {
  @Input() chartKey: string = '';
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  chart!: Chart;
  pte: any[] = [];
  ptb: any[] = [];
  private chartInitialized = false;
  quartPeriod: string[] = [];
  noData: boolean = false;
  chartLabels: string[] = [];

  constructor(private financialService: StockFilterService) { }

  async ngOnInit() {
    await Promise.all([
      this.getActualQuarterlyData('peTTM', 'quarterly', this.pte),
      this.getActualQuarterlyData('pb', 'quarterly', this.ptb),
    ]);
    this.noData =
      this.pte.every((value) => value === null) &&
      this.ptb.every((value) => value === null);

    if (!this.noData && this.chartInitialized) {
      this.updateChart();
    }
  }

  ngAfterViewInit() {
    this.initializeChart();
  }

  private initializeChart() {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (ctx) {
      const groupedData = {
        labels: this.chartLabels.length ? this.chartLabels : ['Q1', 'Q2', 'Q3', 'Q4'],
        datasets: [
          {
            label: 'Price to Earnings',
            data: this.pte.length ? this.pte : [0, 0, 0, 0],
            backgroundColor: 'rgba(112, 218, 208, 1)',
            borderRadius: 10,
          },
          {
            label: 'Price to Book',
            data: this.ptb.length ? this.ptb : [0, 0, 0, 0],
            backgroundColor: 'rgba(48, 131, 255, 1)',
            borderRadius: 10,
          },
        ],
      };

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: groupedData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 30,
            },
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              align: 'start',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                padding: 40,
              },
            },
            // @ts-ignore
            datalabels: {
              anchor: 'end',
              align: 'top',
              display: false,
              formatter: (value: number) => `${value}`,
              font: {
                size: 14,
                weight: 400,
                family: 'Poppins, sans-serif',
              },
              color: '#000',
            },
          },
          scales: {
            x:
            {
              display: true,
              grid: {
                display: false,
              },
            },
            y:
            {
              display: true,
              position: 'right',
              ticks: {
                callback: (value: any) => `${value}`,
              },
              grid: {
                display: true,
                color: '#E7E7E7',
                lineWidth: 3,
                drawBorder: false
              },
            },
          },
        },
      });
      this.chartInitialized = true;
    }
  }

  private updateChart() {
    if (this.chart?.data?.datasets?.length) {
      this.chart.data.datasets[0].data = this.pte;
      this.chart.data.datasets[1].data = this.ptb;
      this.chart.data.labels = this.chartLabels;
      this.chart.update();
    }
  }

  async getActualQuarterlyData(name: string, timePeriod: string, targetArray: any[]) {
    try {
      const ticker = this.chartKey;
      const response = await this.financialService.getRatQuarterlyData(
        ticker,
        this.quartPeriod,
        name,
        timePeriod
      );

      targetArray.length = 0;
      const tempLabels: string[] = [];

      if (response?.hits) {
        // Create array to store all quarters with their dates and values
        const allQuarters: { date: Date; value: number; period: string }[] = [];

        // Process all hits and collect unique quarters
        response.hits
          .filter((hit: Hit) => hit.document.name === name)
          .forEach((hit: Hit) => {
            const periodDate = new Date(hit.document.period);
            const periodStr = hit.document.period;

            // Check if this quarter already exists in our collection
            const exists = allQuarters.some(q => q.period === periodStr);

            if (!exists) {
              allQuarters.push({
                date: periodDate,
                value: hit.document.v,
                period: periodStr
              });
            }
          });

        // Sort quarters by date (oldest first)
        allQuarters.sort((a, b) => a.date.getTime() - b.date.getTime());

        // Take the most recent 4 quarters
        const last4Quarters = allQuarters.slice(-4);

        // Prepare data for chart (oldest to newest)
        last4Quarters.forEach(quarter => {
          const month = quarter.date.getMonth() + 1;
          const year = quarter.date.getFullYear().toString().slice(-2);
          let quarterLabel = '';

          if (month >= 1 && month <= 3) quarterLabel = `Q1 '${year}`;
          else if (month >= 4 && month <= 6) quarterLabel = `Q2 '${year}`;
          else if (month >= 7 && month <= 9) quarterLabel = `Q3 '${year}`;
          else if (month >= 10 && month <= 12) quarterLabel = `Q4 '${year}`;

          targetArray.push(quarter.value);
          tempLabels.push(quarterLabel);
        });

        // Only update chartLabels if this is the first dataset being processed
        if (this.chartLabels.length === 0) {
          this.chartLabels = tempLabels;
        }
      }
    } catch (error) {
      this.noData = true;
      console.error('Error fetching quarterly data:', error);
    }
  }
}
